/**
 * JobApplicantsTable — Excel-like comparison view for a job's applicants.
 *
 * Renders a sortable, filterable, searchable, column-customisable table so
 * recruiters can compare 50+ candidates side-by-side on the fields that
 * actually drive hiring decisions (comp expectations, notice period,
 * relocation, cultural fit, red flags).
 *
 * Data source: the `apps` array already fetched by `JobView.jsx` from
 * `GET /api/jobs/{jobId}/applications` — no new API needed. Each row
 * carries `application + application.candidate + application.analysis +
 * application.budget_status + application.recruiter_rating`. Rich fields
 * come from `candidate.screening_profile` (dedicated screening form) with
 * fall-back to the top-level candidate compensation columns.
 *
 * Column preferences (visibility + order + density) are persisted per-user
 * in localStorage keyed by ``pw:jobApplicantsTable:v2`` so the recruiter's
 * layout survives reload.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Columns3, Download, Eye, Search, Rows3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import EditableScoreBadge from "@/components/EditableScoreBadge";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import { budgetBadgeClass, budgetLabel, formatCompCurrency } from "@/lib/budget";
import { api, formatApiError } from "@/lib/api";
import { useSavedView } from "@/lib/useSavedView";
import { CellHoverCard } from "@/components/CellHoverCard";
import TrackButton from "@/components/TrackButton";
import ResumePreviewButton from "@/components/ResumePreviewButton";
import CandidateCompareDrawer from "@/components/CandidateCompareDrawer";
import QuickTrackCompareDrawer from "@/components/QuickTrackCompareDrawer";
import { BulkActionBar, BulkMessageDialog, BulkNoteDialog, BulkShortlistDialog } from "@/components/jobtable/BulkComponents";
import BulkWhatsAppDialog from "@/components/BulkWhatsAppDialog";
import {
    BulkAssignButton,
    BulkAddToListButton,
    BulkAnalyzeButton,
    BulkRetryButton,
} from "@/components/candtable/CandidateBulkButtons";
import BulkInterviewAssignDialog from "@/components/BulkInterviewAssignDialog";
import BulkPushSkillsDialog from "@/components/BulkPushSkillsDialog";
import { Briefcase as BriefcaseIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "pw:jobApplicantsTable:v3";

// ────────────────────────── Value derivations ──────────────────────────
// Prefer the screening_profile field when the recruiter has entered it,
// falling back to the top-level candidate columns. Numeric coercion is
// forgiving — parseFloat handles "12,00,000" gracefully once we strip
// commas.
const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
};

const pickComp = (c) => {
    const sp = c.screening_profile || {};
    return {
        currency: sp.ctc_currency || c.salary_currency || "INR",
        current: num(sp.current_ctc_annual) ?? num(c.current_salary),
        desired: num(sp.desired_ctc_annual)
            ?? num(c.desired_salary_max)
            ?? num(c.desired_salary_min),
        desired_min: num(c.desired_salary_min),
        desired_max: num(c.desired_salary_max),
        increment_pct: num(sp.desired_increment_pct),
    };
};

const derivedIncrement = (comp) => {
    if (comp.increment_pct !== null && comp.increment_pct !== undefined) return comp.increment_pct;
    if (comp.current && comp.desired) return Math.round(((comp.desired - comp.current) / comp.current) * 1000) / 10;
    return null;
};

// ────────────────────────── Column catalog ──────────────────────────
const COLUMNS = [
    // Identity + score
    { id: "name",             label: "Candidate",       group: "Identity",     width: 220, sticky: true,  get: (r) => r.candidate?.name || "Unknown",
      render: (r, { onOpen, job, siblings, stages, onStageChange }) => (
          <CellHoverCard kind="candidate" row={r} job={job}>
              <div className="flex items-center gap-1.5 leading-tight w-full">
                  <div className="flex flex-col leading-tight min-w-0 flex-1">
                      <button type="button"
                          onClick={(e) => { e.stopPropagation(); onOpen(r); }}
                          className="text-left font-medium text-sm hover:text-ikb truncate"
                          data-testid={`jobtable-open-${r.candidate?.id || r.candidate_id}`}
                          title={r.candidate?.name}>
                          {r.candidate?.name || "Unknown"}
                      </button>
                      <span className="text-[10px] text-muted-foreground truncate" title={r.candidate?.email}>
                          {r.candidate?.email || ""}
                      </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-auto pl-2">
                      <ResumePreviewButton
                          candidate={r.candidate}
                          testId={`jobtable-preview-${r.candidate?.id || r.candidate_id}`}
                          siblings={siblings}
                          stages={stages}
                          onStageChange={onStageChange}
                          onOpenProfile={() => onOpen(r)}
                      />
                      <TrackButton
                          candidateId={r.candidate?.id || r.candidate_id}
                          variant="icon"
                          testId={`jobtable-track-${r.candidate?.id || r.candidate_id}`}
                      />
                  </div>
              </div>
          </CellHoverCard>
      )},
    { id: "score",            label: "Score",           group: "AI",           width: 170, align: "center",
      get: (r) => r.match_score || 0,
      render: (r, { job, onScoreChange }) => (
          <CellHoverCard kind="score" row={r} job={job}>
              <EditableScoreBadge
                  applicationId={r.id}
                  aiScore={r.match_score || 0}
                  manualScore={r.recruiter_score ?? null}
                  size="sm"
                  onChange={(v) => onScoreChange?.(r.id, v)}
              />
          </CellHoverCard>
      )},
    { id: "votes",            label: "Votes",           group: "AI",           width: 180, align: "center",
      get: (r) => r.vote_summary?.counts?.interested || 0,
      render: (r) => (
          <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
              <CandidateVoteBar
                  applicationId={r.id}
                  initial={r.vote_summary}
                  size="sm"
              />
          </div>
      )},
    { id: "stage",            label: "Stage",           group: "Identity",     width: 130,
      get: (r) => r.stage || "Applied",
      render: (r, { renderStage }) => (
          <CellHoverCard kind="stage" row={r}>
              <span className="inline-block w-full">{renderStage ? renderStage(r) : (r.stage || "Applied")}</span>
          </CellHoverCard>
      )},

    // Profile
    { id: "current_title",    label: "Current Title",   group: "Profile",      width: 180, get: (r) => r.candidate?.current_title },
    { id: "current_company",  label: "Current Company", group: "Profile",      width: 160, get: (r) => r.candidate?.current_company },
    { id: "recruiter_summary", label: "Recruiter Summary", group: "Profile",   width: 240,
      get: (r) => r.candidate?.recruiter_summary || "",
      render: (r) => r.candidate?.recruiter_summary ? (
          <span title={r.candidate.recruiter_summary} className="text-xs text-foreground/85 line-clamp-2" data-testid={`jobtable-summary-${r.id}`}>{r.candidate.recruiter_summary}</span>
      ) : <span className="text-muted-foreground">—</span> },
    { id: "location",         label: "Location",        group: "Profile",      width: 140, get: (r) => r.candidate?.location },
    { id: "yoe",              label: "YoE",             group: "Profile",      width: 60,  align: "right",
      get: (r) => r.candidate?.total_experience_years,
      render: (r) => <span className="font-mono text-xs">{r.candidate?.total_experience_years ?? "—"}</span> },
    { id: "phone",            label: "Phone",           group: "Profile",      width: 130, get: (r) => r.candidate?.phone },

    // Compensation
    { id: "currency",         label: "Currency",        group: "Compensation", width: 70, get: (r) => pickComp(r.candidate || {}).currency },
    { id: "current_ctc",      label: "Current CTC",     group: "Compensation", width: 130, align: "right",
      get: (r) => pickComp(r.candidate || {}).current,
      render: (r, { job }) => {
          const c = pickComp(r.candidate || {});
          if (c.current === null) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="ctc" row={r} job={job}>
                  <span className="font-mono text-xs">{formatCompCurrency(c.current, c.currency)}</span>
              </CellHoverCard>
          );
      }},
    { id: "current_ctc_monthly", label: "Current CTC (Monthly)", group: "Compensation", width: 140, align: "right",
      get: (r) => {
          const c = pickComp(r.candidate || {});
          return c.current == null ? null : c.current / 12;
      },
      render: (r) => {
          const c = pickComp(r.candidate || {});
          if (c.current == null) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-xs text-muted-foreground">{formatCompCurrency(c.current / 12, c.currency)}/mo</span>;
      }},
    { id: "desired_ctc",      label: "Desired CTC",     group: "Compensation", width: 130, align: "right",
      get: (r) => pickComp(r.candidate || {}).desired,
      render: (r, { job }) => {
          const c = pickComp(r.candidate || {});
          if (c.desired === null) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="ctc" row={r} job={job}>
                  <span className="font-mono text-xs">{formatCompCurrency(c.desired, c.currency)}</span>
              </CellHoverCard>
          );
      }},
    { id: "desired_ctc_monthly", label: "Desired CTC (Monthly)", group: "Compensation", width: 140, align: "right",
      get: (r) => {
          const c = pickComp(r.candidate || {});
          return c.desired == null ? null : c.desired / 12;
      },
      render: (r) => {
          const c = pickComp(r.candidate || {});
          if (c.desired == null) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-xs text-muted-foreground">{formatCompCurrency(c.desired / 12, c.currency)}/mo</span>;
      }},
    { id: "increment_pct",    label: "Increment %",     group: "Compensation", width: 100, align: "right",
      get: (r) => derivedIncrement(pickComp(r.candidate || {})),
      render: (r, { job }) => {
          const inc = derivedIncrement(pickComp(r.candidate || {}));
          if (inc === null) return <span className="text-muted-foreground">—</span>;
          const cls = inc > 60 ? "text-rose-600" : inc > 30 ? "text-amber-600" : "text-emerald-700";
          const incRounded = Math.round(inc * 100) / 100;
          return (
              <CellHoverCard kind="ctc" row={r} job={job}>
                  <span className={`font-mono text-xs ${cls}`}>{incRounded}%</span>
              </CellHoverCard>
          );
      }},
    { id: "budget",           label: "Budget vs Job",   group: "Compensation", width: 100, align: "center",
      get: (r) => r.budget_status?.status,
      render: (r, { job }) => r.budget_status?.status ? (
          <CellHoverCard kind="budget" row={r} job={job}>
              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${budgetBadgeClass(r.budget_status.status)}`}
                    title={budgetLabel(r.budget_status.status)}>
                  {budgetLabel(r.budget_status.status)}
              </span>
          </CellHoverCard>
      ) : <span className="text-muted-foreground">—</span> },

    // Notice + join
    { id: "on_notice",        label: "On Notice?",      group: "Availability", width: 90,
      get: (r) => (r.candidate?.screening_profile || {}).on_notice_period },
    { id: "notice_days",      label: "Notice (days)",   group: "Availability", width: 100, align: "right",
      get: (r) => (r.candidate?.screening_profile || {}).notice_period_days ?? r.candidate?.notice_period_days,
      render: (r) => {
          const nd = (r.candidate?.screening_profile || {}).notice_period_days ?? r.candidate?.notice_period_days;
          if (nd === null || nd === undefined || nd === "") return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="notice" row={r}>
                  <span className="font-mono text-xs">{nd}</span>
              </CellHoverCard>
          );
      }},
    { id: "earliest_join",    label: "Earliest Join",   group: "Availability", width: 110,
      get: (r) => (r.candidate?.screening_profile || {}).earliest_join_date },
    { id: "relocate",         label: "Relocate?",       group: "Availability", width: 90,
      get: (r) => (r.candidate?.screening_profile || {}).ready_to_relocate
                  ?? (r.candidate?.willing_to_relocate === true ? "Yes" : r.candidate?.willing_to_relocate === false ? "No" : null),
      render: (r) => {
          const sp = (r.candidate?.screening_profile || {});
          const val = sp.ready_to_relocate ?? (r.candidate?.willing_to_relocate === true ? "Yes" : r.candidate?.willing_to_relocate === false ? "No" : null);
          if (!val) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="relocate" row={r}>
                  <span className="text-xs">{typeof val === "boolean" ? (val ? "Yes" : "No") : val}</span>
              </CellHoverCard>
          );
      }},

    // Sourcing
    { id: "source",           label: "Source",          group: "Sourcing",     width: 100,
      get: (r) => (r.candidate?.screening_profile || {}).application_source ?? r.candidate?.application_source ?? r.source },
    { id: "referred_by",      label: "Referred By",     group: "Sourcing",     width: 130,
      get: (r) => (r.candidate?.screening_profile || {}).referred_by_name ?? r.candidate?.referred_by },

    // Cultural + work
    { id: "prev_work_mode",   label: "Prev Work Mode",  group: "Work",         width: 110,
      get: (r) => (r.candidate?.screening_profile || {}).previous_work_mode },
    { id: "offered_work_mode",label: "Offered Mode",    group: "Work",         width: 110,
      get: (r) => (r.candidate?.screening_profile || {}).offered_work_mode },
    { id: "led_team",         label: "Led Team?",       group: "Work",         width: 90,
      get: (r) => (r.candidate?.screening_profile || {}).has_led_team },
    { id: "reason_change",    label: "Reason for Change", group: "Work",       width: 220,
      get: (r) => (r.candidate?.screening_profile || {}).reason_for_job_change,
      render: (r) => {
          const v = (r.candidate?.screening_profile || {}).reason_for_job_change;
          return v ? <span className="text-xs truncate block max-w-[210px]" title={v}>{v}</span> : <span className="text-muted-foreground">—</span>;
      }},
    { id: "cultural_fit",     label: "Cultural Fit",    group: "Work",         width: 90,  align: "center",
      get: (r) => (r.candidate?.screening_profile || {}).cultural_fit_rating,
      render: (r) => {
          const v = (r.candidate?.screening_profile || {}).cultural_fit_rating;
          if (!v) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-xs">{v}/5</span>;
      }},
    { id: "job_hopper",       label: "Job Hopper?",     group: "Work",         width: 100,
      get: (r) => (r.candidate?.screening_profile || {}).is_job_hopper },

    // AI + rating
    { id: "red_flags",        label: "Red Flags",       group: "AI",           width: 90,  align: "center",
      get: (r) => (r.analysis?.red_flags || []).length,
      render: (r) => {
          const n = (r.analysis?.red_flags || []).length;
          const content = n === 0
              ? <span className="text-muted-foreground">0</span>
              : <span className="inline-flex items-center gap-1 text-[11px] text-destructive font-mono">
                    <AlertTriangle className="w-3 h-3" /> {n}
                </span>;
          return <CellHoverCard kind="red_flags" row={r}>{content}</CellHoverCard>;
      }},
    { id: "recruiter_rating", label: "Team Rating",     group: "AI",           width: 100, align: "center",
      get: (r) => r.recruiter_rating?.avg,
      render: (r) => {
          const rr = r.recruiter_rating;
          if (!rr?.avg) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="rating" row={r}>
                  <span className="font-mono text-xs" title={`${rr.count} ratings`}>{rr.avg}/5</span>
              </CellHoverCard>
          );
      }},
    { id: "ai_summary",       label: "AI Summary",      group: "AI",           width: 260,
      get: (r) => r.ai_summary || r.analysis?.summary,
      render: (r) => {
          const s = r.ai_summary || r.analysis?.summary;
          if (!s) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="ai_summary" row={r}>
                  <span className="text-xs truncate block max-w-[250px]" title={s}>{s}</span>
              </CellHoverCard>
          );
      }},

    // Timestamps
    { id: "created_at",       label: "Applied On",      group: "Timeline",     width: 110,
      get: (r) => r.created_at,
      render: (r) => r.created_at ? <span className="font-mono text-xs text-muted-foreground">{r.created_at.slice(0, 10)}</span> : "—" },
];

// Sensible default: focused columns for hiring decisions.
const DEFAULT_VISIBLE_IDS = [
    "name", "score", "votes", "stage",
    "current_title", "recruiter_summary", "location", "yoe",
    "current_ctc", "current_ctc_monthly", "desired_ctc", "desired_ctc_monthly", "increment_pct", "budget",
    "notice_days", "relocate",
    "red_flags", "recruiter_rating", "created_at",
];

/** Convert the persistence shape ({columns:[{id,width,hidden}]}) into
 *  local component state pieces (visibleIds ordered + widths map). */
function fromSavedColumns(saved, allColumns) {
    if (!saved?.columns?.length) return null;
    const visible = [];
    const widths = {};
    const savedIds = new Set();
    for (const s of saved.columns) {
        if (!s?.id) continue;
        savedIds.add(s.id);
        if (!s.hidden && allColumns.some((c) => c.id === s.id)) visible.push(s.id);
        if (s.width) widths[s.id] = s.width;
    }
    // Append any newly-added columns from the catalog that aren't in the saved payload.
    // Keeps upgrades non-breaking — a new column shows up (using its default vis) instead of vanishing.
    return { visibleIds: visible, widths };
}

function toSavedColumns(orderedVisible, allColumns, widths) {
    // Preserve order = visible ids first (in their order), then hidden ids
    // so a re-load reproduces the exact user layout.
    const seen = new Set(orderedVisible);
    const out = orderedVisible.map((id) => ({ id, width: widths[id] || null }));
    for (const c of allColumns) {
        if (!seen.has(c.id)) out.push({ id: c.id, hidden: true, width: widths[c.id] || null });
    }
    return out;
}

function loadPrefs() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            visibleIds: Array.isArray(parsed.visibleIds) ? parsed.visibleIds : null,
            density: parsed.density || "compact",
        };
    } catch { return null; }
}
function savePrefs(p) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* noop */ }
}

// ────────────────────────── Component ──────────────────────────
export default function JobApplicantsTable({
    apps,
    stages = [],
    onOpenProfile,
    onStageChange,
    onRefresh,
    jobTitle = "job",
    jobId,
    job,
}) {
    const initial = loadPrefs() || {};
    const view = useSavedView("job_applicants", {
        columns: DEFAULT_VISIBLE_IDS.map((id) => ({ id })),
        sort: { by: "score", dir: "desc" },
        density: "compact",
    });
    const [visibleIds, setVisibleIds] = useState(initial.visibleIds || DEFAULT_VISIBLE_IDS);
    const [density, setDensity] = useState(initial.density || "compact");
    const [columnWidths, setColumnWidths] = useState({});
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState({ id: "score", dir: "desc" });
    const [budgetFilter, setBudgetFilter] = useState("all");   // all | under | at | over | missing
    const [redFlagFilter, setRedFlagFilter] = useState("all"); // all | with | none
    const [showColPicker, setShowColPicker] = useState(false);
    const pickerRef = useRef(null);
    // Column resize state — the id currently being dragged + its starting width.
    const resizeRef = useRef(null); // {id, startX, startW}
    // B6 — inline bulk-action selection state, keyed by application id.
    // We also keep a Set-form for O(1) lookup during render.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkModal, setBulkModal] = useState(null);   // null | "message" | "note" | "stage" | "shortlist"
    const [bulkBusy, setBulkBusy] = useState(false);
    const [compareOpen, setCompareOpen] = useState(false);
    const [quickCompareOpen, setQuickCompareOpen] = useState(false);
    // Phase 149 — bulk ops carried over from main Candidates module
    const [showBulkInterview, setShowBulkInterview] = useState(false);
    const [showBulkSkills, setShowBulkSkills] = useState(false);
    // Derived list of CANDIDATE ids for the selected applications — the
    // candidate-wide bulk endpoints (/candidates/bulk-*) operate on candidate ids.
    const pickedCandidateIds = useMemo(() => {
        if (selectedIds.size === 0) return [];
        const byId = new Map((apps || []).map((a) => [a.id, a.candidate?.id || a.candidate_id]));
        return [...selectedIds].map((aid) => byId.get(aid)).filter(Boolean);
    }, [selectedIds, apps]);
    const pickedCandidates = useMemo(() => {
        if (selectedIds.size === 0) return [];
        return (apps || []).filter((a) => selectedIds.has(a.id)).map((a) => a.candidate).filter(Boolean);
    }, [selectedIds, apps]);

    // ─── Load remote saved view on mount (once loaded flips true). ─────
    const appliedSavedViewRef = useRef(false);
    useEffect(() => {
        if (!view.loaded || appliedSavedViewRef.current) return;
        appliedSavedViewRef.current = true;
        const restored = fromSavedColumns(view.value, COLUMNS);
        if (restored) {
            if (restored.visibleIds.length) setVisibleIds(restored.visibleIds);
            if (Object.keys(restored.widths).length) setColumnWidths(restored.widths);
        }
        if (view.value.sort?.by) {
            setSortBy({ id: view.value.sort.by, dir: view.value.sort.dir || "desc" });
        }
        if (view.value.density) setDensity(view.value.density);
    }, [view.loaded, view.value]);

    // ─── Save remote view whenever the persisted bits change. ──────────
    useEffect(() => {
        if (!view.loaded) return;
        savePrefs({ visibleIds, density });   // keep legacy localStorage cache
        view.update({
            columns: toSavedColumns(visibleIds, COLUMNS, columnWidths),
            sort: { by: sortBy.id, dir: sortBy.dir },
            density,
        });
    }, [visibleIds, columnWidths, sortBy, density, view.loaded]);

    // ─── Column resize drag handling — starts on grip mousedown. ───────
    const startResize = useCallback((id, e) => {
        e.preventDefault();
        e.stopPropagation();
        const col = COLUMNS.find((c) => c.id === id);
        const startW = columnWidths[id] || col?.width || 120;
        resizeRef.current = { id, startX: e.clientX, startW };
        const onMove = (mv) => {
            if (!resizeRef.current) return;
            const dx = mv.clientX - resizeRef.current.startX;
            const w = Math.max(60, Math.min(500, resizeRef.current.startW + dx));
            setColumnWidths((cur) => ({ ...cur, [resizeRef.current.id]: w }));
        };
        const onUp = () => {
            resizeRef.current = null;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    }, [columnWidths]);

    // ─── Column reorder — move a column's index in visibleIds. ─────────
    const moveColumn = useCallback((id, dir) => {
        setVisibleIds((cur) => {
            const idx = cur.indexOf(id);
            if (idx < 0) return cur;
            const targetIdx = dir === "up" ? idx - 1 : idx + 1;
            if (targetIdx < 0 || targetIdx >= cur.length) return cur;
            const next = cur.slice();
            [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
            return next;
        });
    }, []);

    // Close column-picker on outside click.
    useEffect(() => {
        if (!showColPicker) return;
        const onDown = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowColPicker(false);
        };
        window.addEventListener("mousedown", onDown);
        return () => window.removeEventListener("mousedown", onDown);
    }, [showColPicker]);

    const visibleColumns = useMemo(
        () => visibleIds
            .map((id) => COLUMNS.find((c) => c.id === id))
            .filter(Boolean),
        [visibleIds],
    );

    const toggleColumn = (id) => setVisibleIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    const resetColumns = () => setVisibleIds(DEFAULT_VISIBLE_IDS);
    const showAll = () => setVisibleIds(COLUMNS.map((c) => c.id));

    // Filter + sort
    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return (apps || []).filter((a) => {
            if (needle) {
                const bag = [
                    a.candidate?.name, a.candidate?.email, a.candidate?.current_title,
                    a.candidate?.current_company, a.candidate?.location, a.candidate?.phone,
                    (a.candidate?.skills || []).join(" "),
                    a.candidate?.recruiter_summary,
                    a.ai_summary, a.analysis?.summary,
                ].filter(Boolean).join(" ").toLowerCase();
                if (!bag.includes(needle)) return false;
            }
            if (budgetFilter !== "all") {
                const bs = a.budget_status?.status || null;
                if (budgetFilter === "missing" && bs) return false;
                if (budgetFilter !== "missing" && bs !== budgetFilter) return false;
            }
            if (redFlagFilter !== "all") {
                const rf = (a.analysis?.red_flags || []).length;
                if (redFlagFilter === "with" && rf === 0) return false;
                if (redFlagFilter === "none" && rf > 0) return false;
            }
            return true;
        });
    }, [apps, q, budgetFilter, redFlagFilter]);

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.id === sortBy.id) || COLUMNS[0];
        const dir = sortBy.dir === "asc" ? 1 : -1;
        const list = [...filtered];
        list.sort((a, b) => {
            const va = col.get(a);
            const vb = col.get(b);
            // Nulls always land at the bottom
            const aEmpty = va === null || va === undefined || va === "";
            const bEmpty = vb === null || vb === undefined || vb === "";
            if (aEmpty && !bEmpty) return 1;
            if (!aEmpty && bEmpty) return -1;
            if (aEmpty && bEmpty) return 0;
            if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
            return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
        });
        return list;
    }, [filtered, sortBy]);

    const toggleSort = (id) => setSortBy((cur) => (cur.id === id ? { id, dir: cur.dir === "asc" ? "desc" : "asc" } : { id, dir: "desc" }));

    // ─── Bulk-action selection helpers ───
    const toggleSelect = (aid) => setSelectedIds((cur) => {
        const next = new Set(cur);
        if (next.has(aid)) next.delete(aid); else next.add(aid);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());
    // Select-all references the CURRENT filtered+sorted view, not `apps`.
    const visibleAppIds = useMemo(() => sorted.map((a) => a.id), [sorted]);
    const allViewSelected = visibleAppIds.length > 0 && visibleAppIds.every((id) => selectedIds.has(id));
    const someViewSelected = visibleAppIds.some((id) => selectedIds.has(id));
    const toggleSelectAll = () => setSelectedIds((cur) => {
        if (allViewSelected) {
            const next = new Set(cur);
            for (const id of visibleAppIds) next.delete(id);
            return next;
        }
        return new Set([...cur, ...visibleAppIds]);
    });

    // When the underlying `apps` list changes (e.g. after refresh), prune any
    // selected IDs that no longer exist so the counter never lies.
    useEffect(() => {
        const allIds = new Set((apps || []).map((a) => a.id));
        setSelectedIds((cur) => {
            const filtered = [...cur].filter((id) => allIds.has(id));
            return filtered.length === cur.size ? cur : new Set(filtered);
        });
    }, [apps]);

    // ─── Bulk action executors ───
    const runBulkStage = async (stageKey) => {
        const ids = [...selectedIds];
        if (!ids.length || !stageKey) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/applications/bulk-stage", {
                application_ids: ids,
                stage: stageKey,
            });
            const moved = data?.moved || 0;
            const errors = data?.errors || 0;
            if (errors === 0) toast.success(`Moved ${moved} candidate${moved === 1 ? "" : "s"} → ${stageKey}`);
            else toast.warning(`Moved ${moved} of ${moved + errors} — ${errors} failed`);
            clearSelection();
            setBulkModal(null);
            onRefresh?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };

    const runBulkMessage = async ({ subject, body, template_key, channel }) => {
        // Bulk email uses candidate_ids, not application_ids. Derive from selection.
        const candIds = [...selectedIds]
            .map((aid) => (apps || []).find((a) => a.id === aid))
            .filter(Boolean)
            .map((a) => a.candidate?.id || a.candidate_id)
            .filter(Boolean);
        if (!candIds.length) { toast.error("No valid candidates selected"); return; }
        setBulkBusy(true);
        try {
            const { data } = await api.post("/messages/bulk-send", {
                candidate_ids: candIds,
                subject,
                body,
                template_key: template_key || null,
                channel: channel || "email",
                job_id: jobId || null,
            });
            if ((channel || "email") === "inapp") {
                toast.success(`Posted to ${data?.posted || 0} candidate${(data?.posted || 0) === 1 ? "" : "s"}`);
            } else {
                toast.success(`Email sent to ${data?.sent || 0} · ${data?.no_email || 0} had no email on file`);
            }
            clearSelection();
            setBulkModal(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };

    /**
     * runSaveShortlist — persist the current selection as a named shortlist
     * that hiring managers can view via a public shareable URL. Returns the
     * saved shortlist so the dialog can render the link right after saving.
     */
    const runSaveShortlist = async ({ name, description, is_public }) => {
        const ids = [...selectedIds];
        if (!ids.length || !name?.trim()) return null;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/shortlists", {
                name: name.trim(),
                description: description?.trim() || null,
                application_ids: ids,
                job_id: jobId || null,
                is_public: !!is_public,
            });
            toast.success(`Saved shortlist "${data.name}"`);
            return data;
        } catch (err) { toast.error(formatApiError(err)); return null; }
        finally { setBulkBusy(false); }
    };

    const runBulkNote = async ({ body, pinned }) => {
        // No server-side bulk endpoint for notes — fan out to per-candidate POST
        // in chunks of 6 concurrent requests to stay friendly with rate limits.
        const candIds = [...selectedIds]
            .map((aid) => (apps || []).find((a) => a.id === aid))
            .filter(Boolean)
            .map((a) => a.candidate?.id || a.candidate_id)
            .filter(Boolean);
        if (!candIds.length) { toast.error("No valid candidates selected"); return; }
        setBulkBusy(true);
        let ok = 0, failed = 0;
        try {
            const CHUNK = 6;
            for (let i = 0; i < candIds.length; i += CHUNK) {
                const batch = candIds.slice(i, i + CHUNK);
                const results = await Promise.allSettled(
                    batch.map((cid) => api.post(`/candidates/${cid}/notes`, { body, pinned, job_id: jobId || null })),
                );
                for (const r of results) { if (r.status === "fulfilled") ok += 1; else failed += 1; }
            }
            if (failed === 0) toast.success(`Note added to ${ok} candidate${ok === 1 ? "" : "s"}`);
            else toast.warning(`Added to ${ok} · ${failed} failed`);
            clearSelection();
            setBulkModal(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };

    const renderStage = useCallback((r) => (
        <div onClick={(e) => e.stopPropagation()}>
            {stages.length > 0 ? (
                <select
                    value={r.stage || "Applied"}
                    onChange={(e) => onStageChange?.(r.id, e.target.value)}
                    className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-1 bg-white w-full"
                    data-testid={`jobtable-stage-${r.id}`}
                >
                    {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
            ) : (
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{r.stage || "Applied"}</span>
            )}
        </div>
    ), [stages, onStageChange]);

    const exportCSV = () => {
        const cols = visibleColumns;
        const header = cols.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
        const rows = sorted.map((r) =>
            cols.map((c) => {
                const v = c.get(r);
                if (v === null || v === undefined) return "";
                const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                return `"${s.replace(/"/g, '""')}"`;
            }).join(","),
        );
        const csv = [header, ...rows].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const safe = jobTitle.replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
        a.download = `applicants_${safe}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
    };

    const rowPad = density === "compact" ? "py-1.5" : density === "comfy" ? "py-3" : "py-2";
    const cellPad = density === "compact" ? "px-2" : density === "comfy" ? "px-3" : "px-2.5";

    return (
        <div className={`border border-border bg-white ${selectedIds.size > 0 ? "pb-16" : ""}`} data-testid="job-applicants-table">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-border bg-secondary/20">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search candidates…"
                        className="h-8 pl-7 pr-2 border border-border bg-white text-sm w-64"
                        data-testid="jobtable-search"
                    />
                </div>
                <select
                    value={budgetFilter}
                    onChange={(e) => setBudgetFilter(e.target.value)}
                    className="h-8 border border-border bg-white text-xs px-2 font-mono uppercase tracking-widest"
                    data-testid="jobtable-budget-filter"
                >
                    <option value="all">All budgets</option>
                    <option value="under">Under</option>
                    <option value="at">At</option>
                    <option value="over">Over</option>
                    <option value="missing">Missing</option>
                </select>
                <select
                    value={redFlagFilter}
                    onChange={(e) => setRedFlagFilter(e.target.value)}
                    className="h-8 border border-border bg-white text-xs px-2 font-mono uppercase tracking-widest"
                    data-testid="jobtable-redflag-filter"
                >
                    <option value="all">All flags</option>
                    <option value="with">Red flags ≥ 1</option>
                    <option value="none">No red flags</option>
                </select>

                <div className="ml-auto text-[11px] font-mono text-muted-foreground" data-testid="jobtable-count">
                    {sorted.length} of {(apps || []).length}
                </div>

                <button type="button"
                    onClick={() => setDensity((d) => d === "compact" ? "cozy" : d === "cozy" ? "comfy" : "compact")}
                    className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                    data-testid="jobtable-density"
                    title="Cycle row density">
                    <Rows3 className="w-3 h-3" /> {density}
                </button>

                <div className="relative" ref={pickerRef}>
                    <button type="button"
                        onClick={() => setShowColPicker((v) => !v)}
                        className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                        data-testid="jobtable-column-picker">
                        <Columns3 className="w-3 h-3" /> Columns · {visibleColumns.length}
                    </button>
                    {showColPicker && (
                        <div className="absolute right-0 top-full mt-1 z-40 w-80 max-h-[70vh] overflow-y-auto border border-border bg-white shadow-lg" data-testid="jobtable-column-picker-popover">
                            <div className="p-2 flex items-center gap-2 border-b border-border bg-secondary/30">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-ikb">Layout</span>
                                <button type="button" onClick={showAll} className="ml-auto text-[10px] font-mono text-ikb hover:underline" data-testid="jobtable-columns-all">All</button>
                                <button type="button" onClick={resetColumns} className="text-[10px] font-mono text-ikb hover:underline" data-testid="jobtable-columns-reset">Reset</button>
                            </div>
                            <div className="p-2 border-b border-border">
                                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Order (drag with ↑↓)</div>
                                <ol className="space-y-0.5 max-h-56 overflow-y-auto">
                                    {visibleIds.map((id, idx) => {
                                        const col = COLUMNS.find((c) => c.id === id);
                                        if (!col) return null;
                                        return (
                                            <li key={id} className="flex items-center gap-1 text-xs px-1 py-0.5 border border-border bg-neutral-50" data-testid={`jobtable-order-${id}`}>
                                                <button type="button" disabled={idx === 0 || col.sticky} onClick={() => moveColumn(id, "up")}
                                                    className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:border-ikb disabled:opacity-30"
                                                    aria-label={`Move ${col.label} up`}
                                                    data-testid={`jobtable-move-up-${id}`}>
                                                    <ArrowUp className="w-3 h-3" />
                                                </button>
                                                <button type="button" disabled={idx === visibleIds.length - 1} onClick={() => moveColumn(id, "down")}
                                                    className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:border-ikb disabled:opacity-30"
                                                    aria-label={`Move ${col.label} down`}
                                                    data-testid={`jobtable-move-down-${id}`}>
                                                    <ArrowDown className="w-3 h-3" />
                                                </button>
                                                <span className="ml-1 truncate flex-1">{col.label}</span>
                                                {col.sticky && <span className="text-[8px] font-mono text-muted-foreground">pinned</span>}
                                            </li>
                                        );
                                    })}
                                </ol>
                                <div className="mt-2 flex items-center gap-1">
                                    <button type="button" onClick={async () => {
                                        const r = await view.saveAsCompanyDefault();
                                        if (r.ok) toast.success("Layout saved as company default"); else toast.error(formatApiError(r.error));
                                    }}
                                        className="flex-1 h-7 border border-ikb text-ikb bg-white hover:bg-ikb hover:text-white text-[10px] font-mono uppercase tracking-widest"
                                        data-testid="jobtable-save-company-default">
                                        Save as company default
                                    </button>
                                    <button type="button" onClick={async () => { await view.resetPersonal(); toast.success("Reset — using company default"); }}
                                        className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:border-ikb"
                                        data-testid="jobtable-reset-personal">
                                        Reset mine
                                    </button>
                                </div>
                            </div>
                            <div className="p-2 text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">Show/Hide</div>
                            {[...new Set(COLUMNS.map((c) => c.group))].map((group) => (
                                <div key={group} className="border-b border-border last:border-b-0">
                                    <div className="px-3 py-1.5 bg-secondary/20 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{group}</div>
                                    <div className="p-2 space-y-1">
                                        {COLUMNS.filter((c) => c.group === group).map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/30 px-1 py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleIds.includes(c.id)}
                                                    onChange={() => toggleColumn(c.id)}
                                                    disabled={c.sticky && visibleIds.filter((v) => v === c.id).length}
                                                    className="accent-ikb"
                                                    data-testid={`jobtable-col-toggle-${c.id}`}
                                                />
                                                <span>{c.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button type="button"
                    onClick={exportCSV}
                    className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                    data-testid="jobtable-export-csv">
                    <Download className="w-3 h-3" /> CSV
                </button>
            </div>

            {/* Phase 149 — extended bulk toolbar (parity with the main Candidates module).
                These buttons operate on the CANDIDATE ids behind the selected applications,
                so they can be used to assign the picked people to other jobs, add to a
                hiring list, schedule interviews, push rating skills, re-run AI parsing. */}
            <div className="px-3 py-2 border-t border-border bg-white flex items-center gap-1.5 flex-wrap" data-testid="jobtable-bulk-toolbar">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-1">Bulk</span>
                <BulkAssignButton
                    candidateIds={pickedCandidateIds}
                    onDone={() => { clearSelection(); onRefresh?.(); }}
                    testIdPrefix="jobtable-bulk"
                    excludeJobId={jobId}
                />
                <BulkAddToListButton
                    candidateIds={pickedCandidateIds}
                    onDone={() => clearSelection()}
                    testIdPrefix="jobtable-bulk"
                />
                <Button
                    onClick={() => setShowBulkInterview(true)}
                    disabled={pickedCandidateIds.length === 0}
                    variant="outline" size="sm"
                    className="rounded-none h-8 border-ikb/40 text-ikb hover:bg-ikb/5"
                    data-testid="jobtable-bulk-interview-btn"
                    title="Create interview rounds for selected candidates with one interviewer"
                >
                    <BriefcaseIcon className="w-3.5 h-3.5 mr-1" />
                    Interview · {pickedCandidateIds.length}
                </Button>
                <Button
                    onClick={() => setShowBulkSkills(true)}
                    disabled={pickedCandidateIds.length === 0}
                    variant="outline" size="sm"
                    className="rounded-none h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                    data-testid="jobtable-bulk-skills-btn"
                    title="Push rating skills to upcoming + future interviews for selected candidates"
                >
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    Skills · {pickedCandidateIds.length}
                </Button>
                <BulkAnalyzeButton
                    candidateIds={pickedCandidateIds}
                    onDone={() => { clearSelection(); onRefresh?.(); }}
                    testIdPrefix="jobtable-bulk"
                />
                <BulkRetryButton
                    candidateIds={pickedCandidateIds}
                    items={pickedCandidates}
                    onDone={() => { clearSelection(); onRefresh?.(); }}
                    testIdPrefix="jobtable-bulk"
                />
            </div>

            {/* Table */}
            <div className="overflow-auto pw-thin-scroll max-h-[calc(100vh-240px)]">
                <table className="min-w-full border-collapse text-xs">
                    <thead>
                        <tr className="bg-secondary/40">
                            {/* Selection checkbox column — sticky, always visible */}
                            <th className={`${cellPad} py-2 border-b border-border sticky top-0 lg:left-0 bg-neutral-100 z-30 w-10 min-w-[40px]`}>
                                <input
                                    type="checkbox"
                                    className="accent-ikb cursor-pointer"
                                    checked={allViewSelected}
                                    ref={(el) => { if (el) el.indeterminate = !allViewSelected && someViewSelected; }}
                                    onChange={toggleSelectAll}
                                    aria-label="Select all in view"
                                    data-testid="jobtable-select-all"
                                />
                            </th>
                            {visibleColumns.map((c) => {
                                const effW = columnWidths[c.id] || c.width;
                                return (
                                <th
                                    key={c.id}
                                    onClick={() => toggleSort(c.id)}
                                    className={`relative text-left ${cellPad} py-2 border-b border-border font-mono uppercase tracking-widest text-[10px] cursor-pointer select-none hover:bg-secondary/70 sticky top-0 bg-neutral-100 ${c.sticky ? "lg:sticky z-20" : "z-10"}`}
                                    style={c.sticky
                                        ? { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", left: 40 }
                                        : { minWidth: effW, maxWidth: effW, textAlign: c.align || "left" }}
                                    data-testid={`jobtable-header-${c.id}`}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {c.label}
                                        {sortBy.id === c.id
                                            ? (sortBy.dir === "asc" ? <ArrowUp className="w-3 h-3 text-ikb" /> : <ArrowDown className="w-3 h-3 text-ikb" />)
                                            : <ArrowUpDown className="w-3 h-3 text-muted-foreground/60" />}
                                    </span>
                                    {/* Right-edge resize grip. Absolute positioning + col-resize cursor. */}
                                    <span
                                        role="separator"
                                        aria-orientation="vertical"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => startResize(c.id, e)}
                                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-ikb/30 active:bg-ikb"
                                        data-testid={`jobtable-resize-${c.id}`}
                                        title="Drag to resize"
                                    />
                                </th>
                                );
                            })}
                            <th className={`${cellPad} py-2 border-b border-border w-14 min-w-[56px] lg:sticky lg:right-0 bg-neutral-100 z-10 text-center font-mono uppercase tracking-widest text-[10px]`}>Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr>
                                <td colSpan={visibleColumns.length + 2} className="p-8 text-center text-muted-foreground text-sm">
                                    {(apps || []).length === 0 ? "No candidates yet." : "No candidates match the current filters."}
                                </td>
                            </tr>
                        ) : sorted.map((r) => {
                            const isSelected = selectedIds.has(r.id);
                            return (
                            <tr
                                key={r.id}
                                className={`border-b border-border cursor-pointer group ${isSelected ? "bg-ikb/10 hover:bg-ikb/20" : "hover:bg-secondary/40"}`}
                                onClick={() => onOpenProfile?.(r)}
                                data-testid={`jobtable-row-${r.candidate?.id || r.candidate_id}`}
                            >
                                {/* Selection checkbox */}
                                <td
                                    className={`${cellPad} ${rowPad} w-10 min-w-[40px] lg:sticky lg:left-0 z-[6] ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        className="accent-ikb cursor-pointer"
                                        checked={isSelected}
                                        onChange={() => toggleSelect(r.id)}
                                        aria-label={`Select ${r.candidate?.name || "candidate"}`}
                                        data-testid={`jobtable-select-${r.id}`}
                                    />
                                </td>
                                {visibleColumns.map((c) => {
                                    const effW = columnWidths[c.id] || c.width;
                                    return (
                                    <td
                                        key={c.id}
                                        className={`${cellPad} ${rowPad} border-r border-border/40 ${c.sticky ? `lg:sticky z-[5] ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}` : ""}`}
                                        style={c.sticky
                                            ? { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", verticalAlign: "middle", left: 40 }
                                            : { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", verticalAlign: "middle" }}
                                    >
                                        {c.render
                                            ? c.render(r, {
                                                onOpen: onOpenProfile,
                                                renderStage,
                                                job,
                                                siblings: sorted,
                                                stages,
                                                onStageChange,
                                                onScoreChange: (aid, v) => {
                                                    // Optimistic update: mutate the local row so the
                                                    // manual chip persists until the parent refetches.
                                                    r.recruiter_score = v;
                                                },
                                            })
                                            : formatCell(c.get(r))}
                                    </td>
                                    );
                                })}
                                <td className={`${cellPad} ${rowPad} w-14 min-w-[56px] lg:sticky lg:right-0 z-[5] text-center ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}`}>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onOpenProfile?.(r); }}
                                        className="inline-flex items-center gap-1 h-6 px-1.5 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-[#001f7a]"
                                        data-testid={`jobtable-open-btn-${r.id}`}
                                    >
                                        <Eye className="w-3 h-3" />
                                    </button>
                                </td>
                            </tr>
                        );})}
                    </tbody>
                </table>
            </div>

            {/* Footer hint — helps first-time users discover column picker + CSV */}
            <div className="px-3 py-1.5 border-t border-border bg-secondary/10 text-[10px] font-mono text-muted-foreground flex items-center gap-3 flex-wrap">
                <span>Click any column header to sort.</span>
                <span>·</span>
                <span>Use the Columns menu to show/hide fields.</span>
                <span>·</span>
                <span>Tick a row to bulk-move, message, or note the shortlist.</span>
                <button type="button" onClick={resetColumns} className="ml-auto inline-flex items-center gap-1 hover:text-ikb" data-testid="jobtable-reset-footer">
                    <RefreshCw className="w-3 h-3" /> Reset layout
                </button>
            </div>

            {/* B6 — floating action bar. Only mounted when at least one row
                is selected. Centered near the bottom of the viewport, above
                any other floating widgets. */}
            {selectedIds.size > 0 && (
                <BulkActionBar
                    count={selectedIds.size}
                    stages={stages}
                    onMoveStage={(k) => runBulkStage(k)}
                    onMessage={() => setBulkModal("message")}
                    onInapp={() => setBulkModal("inapp")}
                    onWhatsApp={() => setBulkModal("whatsapp")}
                    onNote={() => setBulkModal("note")}
                    onCompare={() => setCompareOpen(true)}
                    onQuickCompare={() => setQuickCompareOpen(true)}
                    onSaveShortlist={() => setBulkModal("shortlist")}
                    onClear={clearSelection}
                    busy={bulkBusy}
                />
            )}

            {/* B7 — Side-by-side compare drawer. Materializes the selected
                applications as a comparison table. Renders as a full-screen
                overlay only when the user asks for it. */}
            {compareOpen && (
                <CandidateCompareDrawer
                    apps={(apps || []).filter((a) => selectedIds.has(a.id))}
                    job={job}
                    onClose={() => setCompareOpen(false)}
                />
            )}

            {/* B7.5 — Quick Track compare (birds-eye triage drawer). */}
            <QuickTrackCompareDrawer
                open={quickCompareOpen}
                onClose={() => setQuickCompareOpen(false)}
                candidateIds={(apps || [])
                    .filter((a) => selectedIds.has(a.id))
                    .map((a) => a.candidate?.id || a.candidate_id)
                    .filter(Boolean)}
            />

            {/* Bulk-message dialog (Email) */}
            <BulkMessageDialog
                open={bulkModal === "message"}
                count={selectedIds.size}
                onCancel={() => setBulkModal(null)}
                onSend={runBulkMessage}
                busy={bulkBusy}
                mode="email"
            />
            {/* Bulk in-app message dialog */}
            <BulkMessageDialog
                open={bulkModal === "inapp"}
                count={selectedIds.size}
                onCancel={() => setBulkModal(null)}
                onSend={runBulkMessage}
                busy={bulkBusy}
                mode="inapp"
            />
            {/* Bulk WhatsApp (Interakt) dialog */}
            <BulkWhatsAppDialog
                open={bulkModal === "whatsapp"}
                count={selectedIds.size}
                jobId={jobId}
                candidateIds={[...selectedIds]
                    .map((aid) => (apps || []).find((a) => a.id === aid))
                    .filter(Boolean)
                    .map((a) => a.candidate?.id || a.candidate_id)
                    .filter(Boolean)}
                onCancel={() => setBulkModal(null)}
                onDone={() => { setBulkModal(null); clearSelection(); }}
            />
            {/* Bulk-note dialog */}
            <BulkNoteDialog
                open={bulkModal === "note"}
                count={selectedIds.size}
                onCancel={() => setBulkModal(null)}
                onSave={runBulkNote}
                busy={bulkBusy}
            />
            {/* Save-shortlist dialog */}
            <BulkShortlistDialog
                open={bulkModal === "shortlist"}
                count={selectedIds.size}
                onCancel={() => {
                    setBulkModal(null);
                    // Only clear selection when we actually saved something —
                    // Phase 2's Done button is the intended dismiss path.
                }}
                onDone={() => {
                    setBulkModal(null);
                    clearSelection();
                }}
                onSave={runSaveShortlist}
                busy={bulkBusy}
            />

            {/* Phase 149 — bulk Interview + bulk Push Skills (parity with main Candidates module) */}
            <BulkInterviewAssignDialog
                open={showBulkInterview}
                candidateIds={pickedCandidateIds}
                onClose={() => setShowBulkInterview(false)}
                onDone={() => { setShowBulkInterview(false); clearSelection(); onRefresh?.(); }}
            />
            <BulkPushSkillsDialog
                open={showBulkSkills}
                candidateIds={pickedCandidateIds}
                onClose={() => setShowBulkSkills(false)}
                onDone={() => { setShowBulkSkills(false); clearSelection(); onRefresh?.(); }}
            />
        </div>
    );
}

function formatCell(v) {
    if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return <span className="truncate block max-w-full" title={String(v)}>{String(v)}</span>;
}

